import { useState } from "react";
import { Phone, Clock, X } from "lucide-react";
import { trackClick } from "@/lib/clickTracking";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Barra sticky de llamada — solo visible en móvil.
// Al pulsar, abre un pop-up con los horarios y un botón grande para confirmar la llamada.
// Objetivo: máxima conversión desde el móvil sin frustrar al usuario que llama fuera de horario.

const PHONE_HREF = "tel:+34914020331";
const PHONE_LABEL = "914 020 331";

function isSummerHours(date = new Date()) {
  // Mayo (4) a Septiembre (8) incluidos — horario reducido 10:00 – 14:00
  const m = date.getMonth();
  return m >= 4 && m <= 8;
}

function isWithinOpenHours(date = new Date()) {
  const day = date.getDay(); // 0 = domingo, 6 = sábado
  if (day === 0 || day === 6) return false;
  const h = date.getHours();
  return isSummerHours(date) ? h >= 10 && h < 14 : h >= 10 && h < 19;
}

export default function MobileCallBar() {
  const [open, setOpen] = useState(false);
  const summer = isSummerHours();
  const openNow = isWithinOpenHours();

  return (
    <>
      <button
        type="button"
        onClick={() => {
          trackClick("call_intent");
          setOpen(true);
        }}
        data-testid="mobile-call-bar"
        aria-label="Ver horarios y llamar a Club Amistad"
        className="md:hidden fixed top-[68px] inset-x-0 z-40 bg-[#E6217A] hover:bg-[#C81B69] active:bg-[#C81B69] transition-colors text-white shadow-[0_6px_16px_-6px_rgba(230,33,122,0.55)]"
      >
        <div className="flex items-center justify-center gap-3 py-3.5 px-4">
          <Phone className="h-5 w-5 shrink-0" strokeWidth={2.5} />
          <span className="font-outfit font-semibold text-base tracking-tight">
            Llámanos <span className="tabular-nums">{PHONE_LABEL}</span>
          </span>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="call-hours-dialog"
          className="max-w-md p-0 overflow-hidden rounded-2xl border-brand-line bg-brand-bg"
        >
          <div className="bg-[#E6217A] text-white px-6 py-6 relative">
            <button
              onClick={() => setOpen(false)}
              aria-label="Cerrar"
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              data-testid="close-hours-dialog"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-white/15 flex items-center justify-center">
                <Phone className="h-5 w-5" strokeWidth={2.5} />
              </div>
              <div>
                <DialogTitle className="font-outfit text-xl font-semibold leading-tight">
                  Estamos al otro lado del teléfono
                </DialogTitle>
                <DialogDescription className="text-white/85 text-sm mt-0.5">
                  Te atendemos personalmente en horario de oficina.
                </DialogDescription>
              </div>
            </div>
          </div>

          <div className="px-6 py-6 space-y-5">
            <div className="rounded-xl border border-brand-line bg-white p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-brand-primary" />
                <p className="font-outfit font-semibold text-brand-primary text-sm uppercase tracking-wide">
                  Horario de oficina
                </p>
                <span
                  className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium ${
                    openNow
                      ? "bg-green-100 text-green-700"
                      : "bg-brand-line text-brand-muted"
                  }`}
                  data-testid="open-now-status"
                >
                  {openNow ? "Abierto ahora" : "Fuera de horario"}
                </span>
              </div>
              <ul className="space-y-2.5 text-sm">
                <li
                  className={`flex items-baseline justify-between gap-4 ${
                    !summer ? "text-brand-primary font-semibold" : "text-brand-muted"
                  }`}
                >
                  <span>Octubre – Abril</span>
                  <span className="tabular-nums">Lun – Vie · 10:00 – 19:00</span>
                </li>
                <li
                  className={`flex items-baseline justify-between gap-4 ${
                    summer ? "text-brand-primary font-semibold" : "text-brand-muted"
                  }`}
                >
                  <span>Mayo – Septiembre</span>
                  <span className="tabular-nums">Lun – Vie · 10:00 – 14:00</span>
                </li>
              </ul>
            </div>

            <a
              href={PHONE_HREF}
              onClick={() => {
                trackClick("call_now");
                setOpen(false);
              }}
              data-testid="call-now-button"
              className="w-full flex items-center justify-center gap-3 bg-[#E6217A] hover:bg-[#C81B69] active:bg-[#C81B69] transition-colors text-white font-outfit font-semibold text-base rounded-full py-4 px-6 shadow-[0_10px_24px_-8px_rgba(230,33,122,0.6)]"
            >
              <Phone className="h-5 w-5" strokeWidth={2.5} />
              <span>Llamar al {PHONE_LABEL}</span>
            </a>

            <p className="text-xs text-brand-muted text-center leading-relaxed">
              Si nos llamas fuera de horario, puedes solicitar que te llamemos
              desde el formulario de cada viaje.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
